import type {ToolWorkContext} from "./ToolWorkLifecycleTypes";
import BaseToolEngine from "@shared/GenericTools/engines/BaseToolEngine";
import {ToolEngineValidationResult} from "../EngineInterfaces/ToolEngineDataTypes";
import type {ToolDefinitionSnapshotData} from "../EngineInterfaces/ToolEngineDataTypes";
import {getItemListConfigErrors, getItemListPayloadErrors, createItemListSummaryInput, isItemRatingDefinition, createItemListGroupedItems, createItemListOrderedItems, getItemListItemRank, calculateProfileAverage, calculateConfiguredProfileSum, DEFAULT_ITEM_RATING_SCALE_MAX} from "./itemListLifecycleHelpers.js";
import {ToolRendererEnum} from "@shared/GenericTools/ToolRendererEnum.js";
import type {
	EnginePayload,
	ItemCollectionEngineInterface,
	ItemGroupingEngineInterface,
	ItemNotesEngineInterface,
	ItemRankingEngineInterface,
	ItemRatingEngineInterface,
	ItemSelectionEngineInterface,
	ToolConfig,
} from "@shared/GenericTools/EngineInterfaces/ToolEngineInterfaceRegistry";
import {
	ItemCollectionEngineInterfaceToken,
	ItemGroupingEngineInterfaceToken,
	ItemNotesEngineInterfaceToken,
	ItemRankingEngineInterfaceToken,
	ItemRatingEngineInterfaceToken,
	ItemSelectionEngineInterfaceToken,
} from "@shared/GenericTools/EngineInterfaces/ToolEngineInterfaceRegistry";
import type {
	ToolEngineDataValue,
	ToolEngineGroupData,
	ToolEngineItemData,
	ToolInstanceSnapshotData,
	ToolResultSnapshotData,
	ToolEngineStateData,
} from "@shared/GenericTools/EngineInterfaces/ToolEngineDataTypes";

const RATING_DEFINITION = {configRenderer: ToolRendererEnum.PROFILE_DIMENSION_CONFIG}
const DEFAULT_CLIENT_NOTE_TITLE = "Notes about this area"
const DEFAULT_GROUPS = [
	{
		id: "veryImportant",
		title: "Very important",
		description: "This matters strongly right now.",
	},
	{
		id: "important",
		title: "Important",
		description: "This matters, but it is not the strongest priority.",
	},
	{
		id: "notImportant",
		title: "Not important",
		description: "This is not a current priority.",
	},
]

export default class ItemListToolEngine extends BaseToolEngine implements
	ItemCollectionEngineInterface,
	ItemGroupingEngineInterface,
	ItemSelectionEngineInterface,
	ItemRankingEngineInterface,
	ItemRatingEngineInterface,
	ItemNotesEngineInterface {


	isWorkNormalizationStrict(): boolean { return false }
	getWorkNormalizationScope(): string { return "itemListNormalization" }

	normalizeWorkPayload(payload: EnginePayload, config: ToolConfig, context: ToolWorkContext): EnginePayload {
		return this.normalizePayload(payload, config, context.definition)
	}

	finalizeWorkDraft(payload: EnginePayload, config: ToolConfig, context: ToolWorkContext): EnginePayload {
		if (config.ratingProfile) payload.itemList = this.normalizeConfig(config, context.definition)
		return payload
	}

	finalizeWorkSubmission(payload: EnginePayload, config: ToolConfig, context: ToolWorkContext): EnginePayload {
		if (config.ratingProfile) {
			payload.itemList = this.normalizeConfig(config, context.definition)
			this.assertWorkValidation(this.validateWorkPayload(payload, config, context))
		}
		return payload
	}

	validateWorkPayload(payload: EnginePayload, config: ToolConfig, context: ToolWorkContext = {}): ToolEngineValidationResult {
		return this.validateItemListPayload(payload, config, context.definition)
	}

	getEngineInterfaceTokens() {
		return [
			ItemCollectionEngineInterfaceToken,
			ItemGroupingEngineInterfaceToken,
			ItemSelectionEngineInterfaceToken,
			ItemRankingEngineInterfaceToken,
			ItemRatingEngineInterfaceToken,
			ItemNotesEngineInterfaceToken,
		]
	}

	// Renderer policy is explicit: groups, scaleMax and areas can coexist in saved configs.
	normalizeConfig(config: ToolConfig = {}, definition: ToolDefinitionSnapshotData | null = null): ToolConfig {
		if (isItemRatingDefinition(definition)) {
			return this.createToolConfigFromItemRatingConfig(this.createItemRatingConfigFromToolConfig(config))
		}
		return this.createToolConfigFromItemListConfig(this.createItemListConfigFromToolConfig(config))
	}

	createDefaultPayload(config: ToolConfig = {}, definition: ToolDefinitionSnapshotData | null = null): EnginePayload {
		const itemList = this.normalizeConfig(config, definition)
		const items = (itemList.items || []) as ToolConfig[]
		const itemState = isItemRatingDefinition(definition) ? this.createItemRatingStateMap(items) : this.createItemStateMap(items)
		return {itemList, itemState}
	}

	normalizePayload(payload: EnginePayload = {}, config: ToolConfig = {}, definition: ToolDefinitionSnapshotData | null = null): EnginePayload {
		// Snapshots and unknown state keys are historical data, including invalid draft values.
		const itemList = payload.itemList || this.normalizeConfig(config, definition)
		const items = ((itemList as ToolConfig).items || (itemList as ToolConfig).areas || []) as ToolConfig[]
		const itemState = payload.itemState || (isItemRatingDefinition(definition) ? this.createItemRatingStateMap(items) : this.createItemStateMap(items))
		return {...payload, itemList, itemState}
	}

	validateConfig(config: ToolConfig = {}, definition: ToolDefinitionSnapshotData | null = null): ToolEngineValidationResult {
		const result = new ToolEngineValidationResult()
		result.errors = getItemListConfigErrors(config, definition)
		result.isValid = result.errors.length === 0
		return result
	}

	validateItemListPayload(payload: EnginePayload = {}, config: ToolConfig = {}, definition: ToolDefinitionSnapshotData | null = null): ToolEngineValidationResult {
		const result = new ToolEngineValidationResult()
		result.errors = getItemListPayloadErrors(payload, config, definition)
		result.isValid = result.errors.length === 0
		return result
	}

	createSummaryInput(payload: EnginePayload = {}, config: ToolConfig = {}, definition: ToolDefinitionSnapshotData | null = null): EnginePayload {
		return createItemListSummaryInput(payload, definition)
	}

	createItemListConfigFromToolConfig(config: ToolConfig = {}): ToolConfig {
		return {
			title: config.title || "",
			instructions: config.instructions || "",
			progressiveNarrowingConfig: config.progressiveNarrowingConfig || null,
			groups: this.createItemListGroups(config),
			items: ((config.items as ToolConfig[] | undefined) || []).map((item, index) => this.createItemListItem(item, index)),
		}
	}

	createToolConfigFromItemListConfig(itemListConfig: ToolConfig = {}): ToolConfig {
		return {
			title: itemListConfig.title || "",
			instructions: itemListConfig.instructions || "",
			progressiveNarrowingConfig: itemListConfig.progressiveNarrowingConfig || null,
			groups: ((itemListConfig.groups as ToolConfig[] | undefined) || []).map((group, index) => this.createItemListGroup(group, index)),
			items: ((itemListConfig.items as ToolConfig[] | undefined) || []).map((item, index) => this.createItemListItem(item, index)),
		}
	}

	createItemListEnginePayload(enginePayload: EnginePayload = {}, itemListConfig: ToolConfig = {}, itemState: ToolConfig | null = null): EnginePayload {
		let resolvedItemState = itemState || this.createItemStateMap((itemListConfig.items as ToolConfig[] | undefined) || [])
		return this.normalizePayload({
			...enginePayload,
			itemList: this.createToolConfigFromItemListConfig(itemListConfig),
			itemState: resolvedItemState,
		}, itemListConfig)
	}

	createItemListGroup(group: ToolConfig = {}, index = 0): ToolEngineGroupData {
		return {
			id: String(group.id || `group${index + 1}`),
			title: String(group.title || `Group ${index + 1}`),
			description: String(group.description || ""),
		}
	}

	createItemListItem(item: ToolConfig = {}, index = 0): ToolEngineItemData {
		return {
			id: String(item.id || `item${index + 1}`),
			title: String(item.title || `Item ${index + 1}`),
			description: String(item.description || ""),
		}
	}

	updateItemListItemGroup(itemState: ToolConfig = {}, itemId = "", groupId = ""): ToolConfig {
		return {
			...itemState,
			[itemId]: {
				...(itemState[itemId] as ToolConfig || {}),
				groupId,
			},
		}
	}

	createRankedItemState(itemState: ToolConfig = {}, orderedItemIds: string[] = []): ToolConfig {
		return orderedItemIds.reduce((result, itemId, index) => {
			result[itemId] = {
				...(result[itemId] as ToolConfig || {}),
				isSelected: true,
				rank: index + 1,
			}
			return result
		}, {...itemState})
	}

	updateItemListItemSelection(itemState: ToolConfig = {}, itemId = "", isSelected = false): ToolConfig {
		return {
			...itemState,
			[itemId]: {
				...(itemState[itemId] as ToolConfig || {}),
				isSelected,
			},
		}
	}

	ensureItemState(itemState: ToolConfig = {}, items: ToolEngineItemData[] = []): ToolEngineStateData {
		return items.reduce((result, item) => {
			let itemId = String(item.id || "")
			let currentState = (itemState[itemId] as ToolConfig | undefined) || {}
			result[itemId] = {
				groupId: currentState.groupId ?? null,
				rank: currentState.rank ?? null,
				isSelected: !!currentState.isSelected,
				note: currentState.note || "",
			}
			return result
		}, {} as ToolEngineStateData)
	}

	groupItemListItems(itemListConfig: ToolConfig = {}, itemState: ToolConfig = {}): ToolEngineGroupData[] {
		return createItemListGroupedItems(itemListConfig, itemState)
	}

	getItemListUnselectedItems(itemListConfig: ToolConfig = {}, itemState: ToolConfig = {}): ToolEngineItemData[] {
		return ((itemListConfig.items as ToolEngineItemData[] | undefined) || []).filter(item => !(itemState[String(item.id)] as ToolConfig | undefined)?.groupId)
	}

	getItemListSelectedItems(itemListConfig: ToolConfig = {}, itemState: ToolConfig = {}): ToolEngineItemData[] {
		return ((itemListConfig.items as ToolEngineItemData[] | undefined) || []).filter(item => !!(itemState[String(item.id)] as ToolConfig | undefined)?.isSelected)
	}

	getItemListOrderedItems(itemListConfig: ToolConfig = {}, itemState: ToolConfig = {}): ToolEngineItemData[] {
		return createItemListOrderedItems(itemListConfig, itemState)
	}

	getItemListUnrankedItems(itemListConfig: ToolConfig = {}, itemState: ToolConfig = {}): ToolEngineItemData[] {
		return ((itemListConfig.items as ToolEngineItemData[] | undefined) || []).filter(item => {
			let state = (itemState[String(item.id)] as ToolConfig | undefined) || {}
			return state.rank == null && !state.isSelected
		})
	}

	createItemListResultConfig(toolResult: ToolResultSnapshotData | null = null, toolInstance: ToolInstanceSnapshotData | null = null): ToolConfig {
		let summary = (toolResult?.summary as ToolConfig | undefined) || {}
		let config = (summary.itemList as ToolConfig | undefined) || toolInstance?.config || {}
		return this.createItemListConfigFromToolConfig(config)
	}

	createItemListResultState(toolResult: ToolResultSnapshotData | null = null): ToolEngineStateData {
		let summary = (toolResult?.summary as ToolConfig | undefined) || {}
		let enginePayload = (toolResult?.enginePayload as ToolConfig | undefined) || {}
		return (summary.itemState as ToolEngineStateData | undefined) || (enginePayload.itemState as ToolEngineStateData | undefined) || {}
	}

	createItemRatingConfigFromToolConfig(config: ToolConfig = {}): ToolConfig {
		let result: ToolConfig = {
			title: config.title || "",
			instructions: config.instructions || "",
			ratingProfile: config.ratingProfile || null,
			scaleMin: config.scaleMin === 1 ? 1 : 0,
			scaleMax: Number(config.scaleMax) || DEFAULT_ITEM_RATING_SCALE_MAX,
			areas: [],
		}
		result.areas = ((config.items || config.areas || []) as ToolConfig[]).map((area, index) => this.createItemRatingItem(area, index))
		return result
	}

	createToolConfigFromItemRatingConfig(itemRatingConfig: ToolConfig = {}): ToolConfig {
		return {
			title: itemRatingConfig.title || "",
			instructions: itemRatingConfig.instructions || "",
			ratingProfile: itemRatingConfig.ratingProfile || null,
			scaleMin: itemRatingConfig.scaleMin === 1 ? 1 : 0,
			scaleMax: Number(itemRatingConfig.scaleMax) || DEFAULT_ITEM_RATING_SCALE_MAX,
			items: ((itemRatingConfig.areas as ToolConfig[] | undefined) || []).map((area, index) => this.createItemRatingConfigItem(area, index)),
		}
	}

	createItemRatingEnginePayload(enginePayload: EnginePayload = {}, itemRatingConfig: ToolConfig = {}): EnginePayload {
		return this.normalizePayload({
			...enginePayload,
			itemList: this.createToolConfigFromItemRatingConfig(itemRatingConfig),
			itemState: {...(enginePayload.itemState as ToolConfig || {}), ...this.createItemRatingStateMap((itemRatingConfig.areas as ToolConfig[] | undefined) || [])},
		}, itemRatingConfig, RATING_DEFINITION)
	}

	createItemRatingWorkConfig(config: ToolConfig = {}, payload: EnginePayload = {}): ToolConfig {
		const profile = this.createItemRatingConfigFromToolConfig(config)
		const itemState = (payload.itemState as ToolConfig | undefined) || {}
		profile.areas = ((profile.areas as ToolConfig[] | undefined) || []).map(area => {
			const state = (itemState[String(area.id)] as ToolConfig | undefined) || {}
			return {...area, rating: state.rating ?? null, clientNote: state.note || ""}
		})
		return profile
	}

	getItemRatingHistoryItems(configs: ToolConfig[], states: ToolConfig[] = []): ToolConfig[] {
		const itemsById = new Map<string, ToolConfig>()
		for (const config of configs) {
			const items = (config.items || config.areas || []) as ToolConfig[]
			for (const item of items) {
				if (item.id != null && item.id !== "" && !itemsById.has(String(item.id))) itemsById.set(String(item.id), item)
			}
		}
		for (const state of states) {
			for (const id of Object.keys(state)) {
				if (!itemsById.has(id)) itemsById.set(id, {id, title: id})
			}
		}
		return [...itemsById.values()]
	}

	compareItemRatingProfiles(current: EnginePayload = {}, previous: EnginePayload = {}): ToolConfig {
		const currentConfig = (current.itemList as ToolConfig | undefined) || {}
		const previousConfig = (previous.itemList as ToolConfig | undefined) || {}
		const currentState = (current.itemState as ToolConfig | undefined) || {}
		const previousState = (previous.itemState as ToolConfig | undefined) || {}
		const activeIds = new Set(this.getItemRatingHistoryItems([currentConfig]).map(item => String(item.id)))
		const archivedConfig = {areas: currentConfig.archivedAreas || []}
		const items = this.getItemRatingHistoryItems([currentConfig, archivedConfig, previousConfig], [currentState, previousState])
		const rows = items.map(item => {
			const currentRating = (currentState[String(item.id)] as ToolConfig | undefined)?.rating ?? null
			const previousRating = (previousState[String(item.id)] as ToolConfig | undefined)?.rating ?? null
			const hasNumericPair = typeof currentRating === "number" && Number.isFinite(currentRating) && typeof previousRating === "number" && Number.isFinite(previousRating)
			return {id: item.id, title: item.title, isArchived: !activeIds.has(String(item.id)), current: currentRating, previous: previousRating, change: hasNumericPair ? Number(currentRating) - Number(previousRating) : null}
		})
		return {comparable: rows.length > 0, rows}
	}

	createItemRatingResultConfig(toolResult: ToolResultSnapshotData | null = null, toolInstance: ToolInstanceSnapshotData | null = null): ToolConfig {
		const summary = (toolResult?.summary as ToolConfig | undefined) || {}
		const enginePayload = (toolResult?.enginePayload as ToolConfig | undefined) || {}
		const payloadConfig = (enginePayload.itemList as ToolConfig | undefined) || (summary.itemList as ToolConfig | undefined) || {}
		const config = toolInstance?.config || payloadConfig
		const itemState = (enginePayload.itemState as ToolConfig | undefined) || (summary.itemState as ToolConfig | undefined) || {}
		const result = this.createItemRatingConfigFromToolConfig(config)
		const historicalItems = this.getItemRatingHistoryItems([payloadConfig, (summary.itemList as ToolConfig) || {}])
		const historicalById = new Map(historicalItems.map(item => [String(item.id), item]))
		const activeIds = new Set(((result.areas as ToolConfig[]) || []).map(area => String(area.id)))
		const items = this.getItemRatingHistoryItems([result, payloadConfig, (summary.itemList as ToolConfig) || {}], [itemState])
		const resolvedAreas = items.map((item, index) => {
			const area = this.createItemRatingItem(item, index)
			const state = itemState[String(area.id)] as ToolConfig | undefined
			const historical = historicalById.get(String(area.id))
			return {
				...area,
				rating: state ? state.rating ?? null : historical?.rating ?? null,
				clientNote: state ? state.note ?? state.clientNote ?? "" : historical?.clientNote ?? "",
			}
		})
		result.areas = resolvedAreas.filter(area => activeIds.has(String(area.id)))
		result.archivedAreas = resolvedAreas.filter(area => !activeIds.has(String(area.id)))
		return result
	}

	createItemRatingItem(area: ToolConfig = {}, index = 0): ToolEngineItemData {
		return {
			id: String(area.id || `area${index + 1}`),
			title: String(area.title || `Area ${index + 1}`),
			practitionerNote: String(area.practitionerNote || ""),
			rating: area.rating ?? null,
			clientNote: String(area.clientNote || ""),
		}
	}

	getAnsweredItemRatingItems(itemRatingConfig: ToolConfig = {}): ToolEngineItemData[] {
		return ((itemRatingConfig.areas as ToolEngineItemData[] | undefined) || []).filter(area => this.hasItemRating(area))
	}

	getItemRatingAverage(itemRatingConfig: ToolConfig = {}): number | null {
		let answeredAreas = this.getAnsweredItemRatingItems(itemRatingConfig)
		return calculateProfileAverage(answeredAreas)
	}

	getItemRatingConfiguredSum(itemRatingConfig: ToolConfig = {}): number | null {
		const state = this.createItemRatingStateMap((itemRatingConfig.areas as ToolConfig[] | undefined) || [])
		return calculateConfiguredProfileSum(itemRatingConfig, state)
	}

	hasItemRating(area: ToolConfig = {}): boolean {
		return area.rating !== null && area.rating !== undefined && area.rating !== ""
	}

	getItemRatingNoteTitle(area: ToolConfig = {}, scaleMax = DEFAULT_ITEM_RATING_SCALE_MAX): string {
		let result = DEFAULT_CLIENT_NOTE_TITLE
		if (this.hasItemRating(area)) {
			result = `Why I chose ${area.rating}/${scaleMax}`
		}
		return result
	}

	updateItemRating(itemRatingConfig: ToolConfig = {}, itemId = "", rating: ToolEngineDataValue = null): ToolConfig {
		return this.updateItemRatingItem(itemRatingConfig, itemId, {
			rating,
		})
	}

	updateItemRatingItem(itemRatingConfig: ToolConfig = {}, itemId = "", changes: ToolConfig = {}): ToolConfig {
		return {
			...itemRatingConfig,
			areas: ((itemRatingConfig.areas as ToolConfig[] | undefined) || []).map(area => {
				let result = area
				if (area.id === itemId) {
					result = {
						...area,
						...changes,
					}
				}
				return result
			}),
		}
	}

	createItemRatingPreviewConfig(itemRatingConfig: ToolConfig = {}): ToolConfig {
		let scaleMax = Number(itemRatingConfig.scaleMax) || DEFAULT_ITEM_RATING_SCALE_MAX
		return {
			...itemRatingConfig,
			scaleMax,
			areas: ((itemRatingConfig.areas as ToolConfig[] | undefined) || []).map(area => this.createItemRatingPreviewItem(area)),
		}
	}

	createItemStateMap(items: ToolConfig[] = []): ToolConfig {
		return items.reduce((result, item) => {
			let itemId = String(item.id || "")
			result[itemId] = {
				groupId: item.groupId ?? null,
				rank: item.rank ?? null,
				isSelected: !!item.isSelected,
				note: item.note || "",
			}
			return result
		}, {} as ToolConfig)
	}

	createItemListGroups(config: ToolConfig = {}): ToolEngineGroupData[] {
		let configuredGroups = config.groups as ToolConfig[] | undefined
		let sourceGroups = configuredGroups || DEFAULT_GROUPS
		return sourceGroups.map((group, index) => this.createItemListGroup(group, index))
	}

	getItemRank(itemState: ToolConfig = {}, itemId = ""): number {
		return getItemListItemRank(itemState, itemId)
	}

	createItemRatingConfigItem(area: ToolConfig = {}, index = 0): ToolConfig {
		let result = this.createItemRatingItem(area, index)
		delete result.rating
		delete result.clientNote
		return result
	}

	createItemRatingStateMap(areas: ToolConfig[] = []): ToolConfig {
		return areas.reduce((result, area) => {
			let areaId = String(area.id || "")
			result[areaId] = {
				rating: area.rating ?? null,
				note: area.clientNote || "",
			}
			return result
		}, {} as ToolConfig)
	}

	createItemRatingPreviewItem(area: ToolConfig = {}): ToolConfig {
		return {
			...area,
			rating: null,
			clientNote: "",
		}
	}
}
